(function () {
  'use strict';

  if (customElements.get('press-component')) {
    return;
  }

  customElements.define(
    'press-component',
    class PressComponent extends HTMLElement {
      constructor() {
        super();

        this.slider = this.querySelector('[data-press-items]');
        this.sliderNav = this.querySelector('[data-logo-slider]');

        this.checkSlidesEvent = () => this.checkSlides();
        this.sliderResizeEvent = () => this.resizeSlider();

        this.sliderInitialized = false;
        this.lazyInitObserver = null;
      }

      connectedCallback() {
        const isHomepage =
          document.body &&
          document.body.classList.contains('template-index');

        const isThemeEditor =
          window.Shopify &&
          window.Shopify.designMode;

        if (
          isHomepage &&
          !isThemeEditor &&
          'IntersectionObserver' in window
        ) {
          this.lazyInitObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                this.lazyInitObserver.disconnect();
                this.lazyInitObserver = null;
                this.activatePressSlider();
              });
            },
            {
              root: null,
              rootMargin: '0px',
              threshold: 0.01,
            }
          );

          this.lazyInitObserver.observe(this);
          return;
        }

        this.activatePressSlider();
      }

      activatePressSlider() {
        if (this.sliderInitialized) return;
        this.sliderInitialized = true;

        this.resizeSlider();

        document.addEventListener(
          'theme:resize:width',
          this.checkSlidesEvent
        );

        document.addEventListener(
          'theme:resize:width',
          this.sliderResizeEvent
        );
      }

      /**
       * Controls whether the logo carousel should be draggable.
       *
       * MOBILE:
       * Always enable dragging/wrap-around when there are 3+ logos.
       * This avoids relying on width measurements during initial page load,
       * which can be unreliable on some Android/Chrome devices.
       *
       * DESKTOP:
       * Preserve the original behavior and only enable dragging when the
       * combined logo width is larger than the available section width.
       */
      checkSlides() {
        const section = this.closest('section');
        const slides = this.querySelectorAll('[data-logo-slide]');
        const flickity =
          window.theme.Flickity.data(this.sliderNav) || null;

        if (!section || flickity === null) {
          return;
        }

        const sectionWidth = section.offsetWidth;
        const isMobile = window.innerWidth <= 767;

        /*
         * Reset to the non-scrolling state first.
         */
        flickity.options.draggable = false;
        flickity.options.wrapAround = false;
        flickity.options.contain = true;

        /*
         * MOBILE
         *
         * With 3 or more logos, always make the carousel swipeable.
         * Do not depend on a width calculation during initial load.
         */
        if (isMobile && slides.length > 2) {
          flickity.options.draggable = true;
          flickity.options.wrapAround = true;
          flickity.options.contain = false;
        }

        /*
         * DESKTOP
         *
         * Retain the original behavior:
         * only scroll when the logos actually overflow.
         */
        if (
          !isMobile &&
          slides.length > 2 &&
          this.getSlidesWidth() > sectionWidth
        ) {
          flickity.options.draggable = true;
          flickity.options.wrapAround = true;
          flickity.options.contain = false;
        }

        flickity.resize();
        flickity.updateDraggable();
      }

      /**
       * Returns the combined rendered width of all logo slides.
       */
      getSlidesWidth() {
        const slides = this.querySelectorAll('[data-logo-slide]');
        let totalWidth = 0;

        if (slides.length) {
          slides.forEach((slide) => {
            totalWidth += slide.offsetWidth;
          });
        }

        return totalWidth;
      }

      /**
       * Keeps the visual logo highlight synchronized with the active
       * press/content slide.
       *
       * Flickity's asNavFor normally supplies .is-selected automatically,
       * but when this component is initialized well before it enters the
       * viewport that state can be unreliable on desktop. The section CSS
       * uses .is-selected to raise the active logo from the configured grey
       * opacity to full opacity, so explicitly maintaining the class keeps
       * the original 3-second highlight behavior deterministic.
       */
      syncLogoSelection(selectedIndex) {
        const logoSlides = this.querySelectorAll('[data-logo-slide]');

        logoSlides.forEach((logoSlide, index) => {
          logoSlide.classList.toggle(
            'is-selected',
            index === selectedIndex
          );
        });
      }

      /**
       * Homepage mobile logo-only mode.
       *
       * The homepage Press section has no quote/content text. The old setup
       * autoplays an empty Flickity instance and makes the logo carousel follow
       * it through asNavFor. Mobile Safari can fail to advance that linked
       * pair. In logo-only mode, autoplay the visible logo carousel directly.
       * This also avoids the theme's document-level touchmove override, which
       * can interfere with Safari touch gestures.
       */
      initMobileLogoSlider(autoplay) {
        let sliderNav =
          window.theme.Flickity.data(this.sliderNav) || null;

        if (!sliderNav) {
          sliderNav = new window.theme.Flickity(this.sliderNav, {
            draggable: true,
            wrapAround: true,
            contain: false,
            imagesLoaded: true,
            prevNextButtons: false,
            adaptiveHeight: false,
            pageDots: false,
            autoPlay: autoplay,
            pauseAutoPlayOnHover: false,

            on: {
              ready: () => {
                requestAnimationFrame(() => {
                  if (!sliderNav) return;
                  sliderNav.resize();
                  this.checkSlides();
                  this.syncLogoSelection(sliderNav.selectedIndex || 0);
                });
              },
            },
          });

          sliderNav.on('change', (index) => {
            this.syncLogoSelection(index);
          });

          /*
           * Flickity stops autoplay after pointer interaction. Safari can
           * interpret a normal touch/scroll gesture as pointer interaction,
           * so resume the player after the gesture ends.
           */
          const resumeAutoplay = () => {
            if (autoplay && typeof sliderNav.playPlayer === 'function') {
              sliderNav.playPlayer();
            }
          };

          sliderNav.on('pointerUp', resumeAutoplay);
          sliderNav.on('dragEnd', resumeAutoplay);
        } else {
          sliderNav.resize();
          this.checkSlides();
        }
      }

      /**
       * Initializes both Flickity instances:
       *
       * - Main quote/content slider
       * - Logo navigation slider
       */
      initSlider() {
        let slider =
          window.theme.Flickity.data(this.slider) || null;

        let sliderNav =
          window.theme.Flickity.data(this.sliderNav) || null;

        const duration = parseInt(this.dataset.duration, 10);

        const autoplay =
          this.dataset.autoplay === 'true' && duration;

        const section = this.closest('section');
        const logoOnly =
          section && section.classList.contains('press--no-blockquotes');
        const isMobile = window.innerWidth <= 767;

        /*
         * On the homepage mobile Press section there is no quote/content text.
         * Autoplay the visible logo carousel itself instead of linking it to an
         * empty hidden carousel. This is both simpler and more reliable in
         * Mobile Safari.
         */
        if (logoOnly && isMobile) {
          this.initMobileLogoSlider(autoplay);
          return;
        }

        /*
         * Main content/quote slider
         */
        slider = new window.theme.Flickity(this.slider, {
          fade: true,
          wrapAround: true,
          adaptiveHeight: false,
          prevNextButtons: false,
          pageDots: false,
          autoPlay: autoplay,

          on: {
            ready: () => {
              requestAnimationFrame(() => {
                if (slider) {
                  slider.resize();
                }
              });
            },
          },
        });

        /*
         * Logo slider
         *
         * pageDots is intentionally FALSE.
         * Mobile dragging is enabled by checkSlides().
         */
        sliderNav = new window.theme.Flickity(this.sliderNav, {
          draggable: false,
          wrapAround: false,
          contain: true,
          imagesLoaded: true,
          asNavFor: this.slider,
          prevNextButtons: false,
          adaptiveHeight: false,
          pageDots: false,

          on: {
            ready: () => {
              this.querySelectorAll('[data-logo-slide]').forEach(
                (logoSlide) => {
                  logoSlide.addEventListener('keyup', (event) => {
                    if (
                      event.code === theme.keyboardKeys.ENTER ||
                      event.code ===
                        theme.keyboardKeys.NUMPADENTER ||
                      event.code === theme.keyboardKeys.SPACE
                    ) {
                      const index = Number(
                        logoSlide.getAttribute('data-logo-index')
                      );

                      slider.selectCell(index);
                    }
                  });
                }
              );

              /*
               * Resize once Flickity has created its internal cells.
               */
              requestAnimationFrame(() => {
                if (sliderNav) {
                  sliderNav.resize();
                }

                /*
                 * Run our mobile/desktop carousel logic immediately
                 * after Flickity is ready.
                 */
                this.checkSlides();

                /*
                 * Guarantee an active desktop logo immediately.
                 */
                this.syncLogoSelection(
                  slider ? slider.selectedIndex : 0
                );
              });
            },
          },
        });

        window.theme.flickitySmoothScrolling(this.slider);
        window.theme.flickitySmoothScrolling(this.sliderNav);

        /*
         * Clicking/swiping a logo changes the matching content slide.
         */
        sliderNav.on('change', (index) => {
          slider.selectCell(index);
          this.syncLogoSelection(index);
        });

        /*
         * Keep links/buttons keyboard-accessible only on the
         * currently selected content slide.
         */
        slider.on('change', (selectedIndex) => {
          /*
           * Keep the matching logo fully opaque as autoplay advances.
           */
          this.syncLogoSelection(selectedIndex);

          slider.cells.forEach((cell, index) => {
            cell.element
              .querySelectorAll('a, button')
              .forEach((element) => {
                element.setAttribute(
                  'tabindex',
                  index === selectedIndex ? '0' : '-1'
                );
              });
          });
        });
      }

      /**
       * Reinitializes/resizes safely when the viewport changes.
       */
      resizeSlider() {
        const slider =
          window.theme.Flickity.data(this.slider) || null;

        const sliderNav =
          window.theme.Flickity.data(this.sliderNav) || null;

        if (slider && sliderNav) {
          slider.resize();
          sliderNav.resize();

          /*
           * Re-evaluate mobile/desktop scrolling after resize.
           */
          this.checkSlides();
        } else {
          this.initSlider();

          /*
           * A second check after layout settles gives Android Chrome
           * another opportunity to report final dimensions correctly.
           * Mobile does not depend on this measurement, but it remains
           * useful for desktop overflow detection.
           */
          setTimeout(() => {
            this.checkSlides();
          }, 300);
        }
      }

      disconnectedCallback() {
        if (this.lazyInitObserver) {
          this.lazyInitObserver.disconnect();
          this.lazyInitObserver = null;
        }

        document.removeEventListener(
          'theme:resize:width',
          this.checkSlidesEvent
        );

        document.removeEventListener(
          'theme:resize:width',
          this.sliderResizeEvent
        );
      }
    }
  );
})();